import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { apiErr, resolveAvatar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, StatChip, ProgressBar, DynIcon } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, refetch } = useQuery({ queryKey: ["student-profile", id], queryFn: async () => (await api.get(`/students/${id}/profile`)).data });
  const { data: badges } = useQuery({ queryKey: ["badges"], queryFn: async () => (await api.get("/badges")).data });
  const [amount, setAmount] = useState(50);
  const [reason, setReason] = useState("");

  if (isLoading || !data) return <Loader />;
  const s = data.stats;

  const award = async () => {
    const fd = new FormData(); fd.append("amount", amount); fd.append("reason", reason || "Special award");
    await api.post(`/users/${id}/award`, fd);
    toast.success(`+${amount} Story Points awarded!`); setReason(""); refetch();
  };
  const giveBadge = async (badgeId) => {
    const r = await api.post(`/badges/${badgeId}/award/${id}`);
    toast[r.data.awarded ? "success" : "info"](r.data.awarded ? "Badge awarded! 🏅" : "Already has this badge"); refetch();
  };
  const resetPw = async () => { const r = await api.post(`/users/${id}/reset-password`); toast.success(`Password reset to: ${r.data.new_password}`); };

  return (
    <div className="space-y-5" data-testid="student-detail">
      <button onClick={() => navigate("/staff/students")} className="flex items-center gap-1 text-sm font-bold text-csh-purple"><Icons.ChevronLeft size={16} /> Students</button>
      <div className="rounded-3xl gradient-purple p-6 text-white flex items-center gap-4">
        <img src={resolveAvatar(s.avatar)} alt="" className="h-20 w-20 rounded-3xl object-cover ring-4 ring-white/30" />
        <div className="flex-1">
          <h1 className="font-head text-3xl">{s.name}</h1>
          <p className="text-white/80">{s.grade}{s.gender ? ` · ${s.gender}` : ""} · {s.level_name}</p>
        </div>
        {user.role === "admin" && <Button onClick={resetPw} className="rounded-full bg-white/15 text-white font-bold" data-testid="reset-pw-btn"><Icons.KeyRound size={16} /> Reset PW</Button>}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatChip icon="Star" value={s.points} label="Points" color="#EAB308" />
        <StatChip icon="Medal" value={s.badges_count} label="Badges" color="#662D91" />
        <StatChip icon="Target" value={s.quests_done} label="Quests" color="#FC633B" />
        <StatChip icon="CalendarCheck" value={`${s.attendance_pct}%`} label="Attendance" color="#22C55E" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
          <h3 className="font-head text-xl text-csh-blue mb-3">Give Special Award</h3>
          <div className="flex gap-2">
            <Input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="w-24 rounded-xl border-2" data-testid="award-amount" />
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className="flex-1 rounded-xl border-2" data-testid="award-reason" />
            <Button onClick={award} className="rounded-full gradient-orange text-white font-bold" data-testid="award-btn"><Icons.Gift size={16} /></Button>
          </div>
          <div className="mt-3">
            <Dialog>
              <DialogTrigger asChild><Button variant="outline" className="rounded-full w-full" data-testid="award-badge-btn"><Icons.Medal size={16} /> Award a Badge</Button></DialogTrigger>
              <DialogContent className="rounded-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-head text-2xl text-csh-blue">Award a Badge</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-2">
                  {(badges || []).map((b) => (
                    <button key={b.id} onClick={() => giveBadge(b.id)} className="flex items-center gap-2 rounded-2xl border-2 border-black/10 p-3 hover:border-csh-purple text-left">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full" style={{ backgroundColor: b.color + "22" }}><DynIcon name={b.icon} size={18} color={b.color} /></div>
                      <span className="text-sm font-bold text-csh-blue">{b.name}</span>
                    </button>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
          <h3 className="font-head text-xl text-csh-blue mb-2">Performance Readiness</h3>
          <p className="text-3xl font-head text-csh-purple">{s.performance_readiness}%</p>
          <ProgressBar pct={s.performance_readiness} className="mt-2" />
          <h4 className="font-head text-lg text-csh-blue mt-4 mb-2">Badges</h4>
          <div className="flex flex-wrap gap-2">
            {data.badges.map((b) => (
              <div key={b.id} className="flex h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: (b.badge?.color || "#662D91") + "22" }} title={b.badge?.name}>
                <DynIcon name={b.badge?.icon} size={18} color={b.badge?.color || "#662D91"} />
              </div>
            ))}
            {data.badges.length === 0 && <p className="text-sm text-muted-foreground">No badges yet.</p>}
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
        <h3 className="font-head text-xl text-csh-blue mb-3">Points History</h3>
        <div className="space-y-1.5">
          {data.ledger.map((t) => (
            <div key={t.id} className="flex items-center justify-between text-sm border-b border-black/5 py-1.5">
              <span className="text-csh-grey">{t.reason}</span>
              <span className={t.amount >= 0 ? "font-bold text-green-600" : "font-bold text-red-500"}>{t.amount >= 0 ? "+" : ""}{t.amount}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
